import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, forkJoin, Observable, of, ReplaySubject, Subject } from "rxjs";
import { catchError, filter, switchMap, take, takeUntil } from "rxjs/operators";
import * as moment from "moment";

import { DateHelper } from "@app/core/helpers/date.helper";
import { ScoreLocalService } from "@app/pages/score/score.service";
import { LastScoreResponse } from "src/swagger/api.client.generated";
import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStep, OnboardingStepService, parseStepTimestamp } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { SCORE_SESSION_ROUTE } from "./h01-home-introduction.service";
import { isOnRoute, whenUiQuiet } from "./onboarding-lesson-modal";
import { SCORE_LANDING_ROUTE } from "./s01-score-introduction.service";
import { ScoreContextualEntryService } from "./score-contextual-entry.service";

export const RS01_LESSON_ID = "RS01";
export const RS02_LESSON_ID = "RS02";

/**
 * RS01 and RS02 share one lesson state and one deferral count (FLIP-366), so both routes read and
 * write this one row - RS02 never gets a row of its own. Tracking still names the route shown.
 */
export const REPEAT_SCORING_STEP_KEY = RS01_LESSON_ID;

/** Recorded with the shared row's completed state - its CompletedAt *is* this milestone. */
export const SCORE_REPEAT_SCORING_INTRODUCED = "score_repeat_scoring_introduced";

/** The third explicit deferral, on either route or across both, dismisses the lesson for both. */
export const RS_MAX_DEFERRALS = 3;

/** RS01's minimum_delay_elapsed: the current Score must be at least an hour old. */
export const RS01_MINIMUM_DELAY_MS = 60 * 60 * 1000;

/** Anchors workbook v2.38: the Buffer view's Score again control. */
export const RS01_ANCHOR = "score.score_again";

/**
 * Where the Score tab sends a user who has a current Score (ScoreLocalService.shouldShowMarginOfSafety)
 * - "they return to Score and see the current Buffer with Score again". Not /score/buffer-result,
 * which is where a Score submission lands.
 */
export const RETURNING_BUFFER_ROUTE = "/score/marginofsafety";

/** Copy rows RS01.01-RS01.04 / RS02.01-RS02.04, workbook v2.38 Copy sheet (verbatim, identical for both). */
export const RS_COPY = {
  heading: "Your Safety Buffer can change",
  body:
    "Your Score reflects what is supporting you and the demands your system is carrying at that " +
    "moment. These can change throughout the day. Scoring again helps you check in with your " +
    "Safety Buffer and notice whether it has shifted since your last Score.",
  primaryLabel: "Score again",
  secondaryLabel: "Not now",
};

/** What the Score landing page renders for RS02. */
export interface RS02Card {
  heading: string;
  body: string;
  primaryLabel: string;
  secondaryLabel: string;
}

type RouteId = typeof RS01_LESSON_ID | typeof RS02_LESSON_ID;

/**
 * Onboarding lessons RS01 / RS02 "Your Safety Buffer can change" (FLIP-368 / FLIP-369; spec v2.36
 * §18.1, workbook v2.38). One Task lesson with two routes:
 *
 * - RS01 - anchored prompt (with buttons) at Score again on the returning Buffer view
 *   (/score/marginofsafety), when a current Score (six-hour or 4 am rule) exists and is at least an
 *   hour old. MarginOfSafetyComponent queues it after B01/B02.
 * - RS02 - explanation card (task variant) on the Score landing page, when today's Score (since the
 *   3:59am app-day boundary) has already passed its six-hour reset. The overnight 4 am reset alone
 *   never triggers it: with no Score since the boundary it isn't eligible. First in the landing
 *   page's queue (ScoreLandingNewComponent).
 *
 * Shared rules:
 * - Eligible while the `onboarding-walkthrough` flag is on and the shared row is not completed,
 *   dismissed, at the deferral cap or deferred. Any failed check holds back. Only once the UI is
 *   quiet (recovery and blocking modals first) and the user is still on the route.
 * - Score again - the prompt/card button, or the real control while RS01 shows - completes the
 *   lesson and continues to the new Score: RS01 goes to /score as the real control does, RS02 opens
 *   the scoring session. Genuinely starting a new Score after the lesson has been presented also
 *   completes it (onNewScoreStarted). A never-presented lesson is not completed that way, or it
 *   would retire before most users could see it.
 * - Not now and the visible close record one deferral each: back no earlier than the next app day
 *   (so neither route re-appears this entry or today); the third, on either route, dismisses both.
 * - Leaving the surface hides the prompt/card and records nothing.
 * - Existing users: no backfill - eligible whenever the normal trigger next occurs.
 */
@Injectable({ providedIn: "root" })
export class RepeatScoringService {
  /** The RS02 card showing on the Score landing page, or null. */
  readonly card$ = new BehaviorSubject<RS02Card | null>(null);

  private readonly cancel$ = new Subject<void>();
  private outcome$: ReplaySubject<boolean> | null = null;
  /** The shared row as last read, for the deferral count. */
  private step: OnboardingStep | null = null;
  /** Completed or dismissed in this app session - its write may still be in flight. */
  private resolved = false;
  private promptOpen = false;

  constructor(
    private router: Router,
    private coachMarkService: CoachMarkService,
    private blockingUiSignal: BlockingUiSignalService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private scoreLocalService: ScoreLocalService,
    private entry: ScoreContextualEntryService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this account's state.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.resolved = false;
      this.step = null;
      this.leave();
    });
  }

  /** Whether the RS02 card is showing. */
  get isCardShowing(): boolean {
    return this.card$.value !== null;
  }

  /** RS01: call once the returning Buffer view has loaded and B01/B02 are out of the way. */
  maybeShowAtScoreAgain(): void {
    this.cancelPending();
    this.check(RS01_LESSON_ID, RETURNING_BUFFER_ROUTE).subscribe((step) => {
      if (step !== false) this.openPrompt(step);
    });
  }

  /**
   * RS02: call on arrival at the Score landing page. Emits once - true if the card was presented,
   * false if not - so the landing page's queue can move on. Never emits if the user leaves first.
   */
  maybeShowOnLanding(): Observable<boolean> {
    this.cancelPending();
    const outcome$ = new ReplaySubject<boolean>(1);
    this.outcome$ = outcome$;
    this.check(RS02_LESSON_ID, SCORE_LANDING_ROUTE).subscribe({
      next: (step) => {
        if (step === false) {
          this.settle(false);
          return;
        }
        this.presentCard(step);
        this.settle(true);
      },
      // The flag being off completes the check without a value - the queue still moves on.
      complete: () => this.settle(false),
    });
    return outcome$.pipe(take(1));
  }

  /** The real Score again control on the Buffer view - completes the lesson while RS01 shows. */
  onScoreAgainSelected(): void {
    if (this.promptOpen) {
      this.promptOpen = false;
      this.coachMarkService.dismiss();
      this.track(RS01_LESSON_ID, "onboarding_lesson_action", "action: score_again_control");
      this.complete(RS01_LESSON_ID);
    }
  }

  /** RS02 card: Score again - completes, then opens the scoring session. */
  cardScoreAgain(): void {
    if (!this.isCardShowing) return;
    this.card$.next(null);
    this.track(RS02_LESSON_ID, "onboarding_lesson_action", "action: score_again");
    this.complete(RS02_LESSON_ID);
    this.scoreLocalService.markScoreAgainEntry();
    this.router.navigate([SCORE_SESSION_ROUTE]);
  }

  /** RS02 card: Not now. */
  cardNotNow(): void {
    if (!this.isCardShowing) return;
    this.card$.next(null);
    this.track(RS02_LESSON_ID, "onboarding_lesson_action", "action: not_now");
    this.defer(RS02_LESSON_ID);
  }

  /** RS02 card: the visible close - a deferral for this task variant (§26.4). */
  cardClose(): void {
    if (!this.isCardShowing) return;
    this.card$.next(null);
    this.track(RS02_LESSON_ID, "onboarding_lesson_action", "action: close");
    this.defer(RS02_LESSON_ID);
  }

  /**
   * Call when a genuinely new scoring session starts (not a recovered draft). Completes the lesson
   * if it has been presented and is still unresolved.
   */
  onNewScoreStarted(): void {
    if (this.resolved) return;
    this.entry
      .enabled$()
      .pipe(switchMap(() => this.onboardingStepService.getStep(REPEAT_SCORING_STEP_KEY)))
      .subscribe((step) => {
        if (this.resolved || !step?.startedAt || step.completedAt || step.dismissed) return;
        this.step = step;
        this.complete(RS01_LESSON_ID, "new_score_started");
      });
  }

  /** Call when leaving either surface - hides the prompt/card and abandons a pending check. */
  leave(): void {
    this.cancelPending();
    if (this.promptOpen) {
      this.promptOpen = false;
      this.coachMarkService.dismiss();
    }
    if (this.isCardShowing) {
      this.card$.next(null);
    }
  }

  /**
   * Completes without emitting whatever was waiting on this service, then abandons its pending
   * check. The outcome is detached first, so the abandoned check's completion can't settle it.
   */
  private cancelPending(): void {
    const outcome$ = this.outcome$;
    this.outcome$ = null;
    outcome$?.complete();
    this.cancel$.next();
  }

  /**
   * Emits once: the shared row (null: none yet) when `route`'s trigger is met and it may show now,
   * or false when it may not. Completes without emitting when the flag is off or on cancelPending().
   */
  private check(route: RouteId, surface: string): Observable<OnboardingStep | null | false> {
    return this.entry.enabled$().pipe(
      switchMap(() =>
        forkJoin([
          this.onboardingStepService.getStep(REPEAT_SCORING_STEP_KEY),
          // null = no Score; undefined = the check failed.
          this.scoreLocalService.getLastScore().pipe(
            take(1),
            catchError(() => of(undefined))
          ),
        ])
      ),
      switchMap(([step, score]) => {
        if (!this.isEligible(route, step, score)) {
          return of(false as const);
        }
        return whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$).pipe(
          // Re-checked at open time: the user may have moved on, or another lesson taken the entry.
          switchMap(() =>
            of(
              isOnRoute(this.router, surface) &&
                !this.resolved &&
                !this.entry.presentedThisEntry(RS01_LESSON_ID, RS02_LESSON_ID)
                ? (step ?? null)
                : (false as const)
            )
          )
        );
      }),
      take(1),
      takeUntil(this.cancel$)
    );
  }

  private isEligible(
    route: RouteId,
    step: OnboardingStep | null | undefined,
    score: LastScoreResponse | null | undefined
  ): boolean {
    if (this.resolved || step === undefined || score === undefined || score === null) {
      return false;
    }
    if (step?.completedAt || step?.dismissed || (step?.deferralCount ?? 0) >= RS_MAX_DEFERRALS) {
      return false;
    }
    const deferredUntil = parseStepTimestamp(step?.deferredUntil);
    if (deferredUntil && deferredUntil > new Date()) {
      return false;
    }
    if (route === RS01_LESSON_ID) {
      const ageMs = moment().diff(moment(score.insertLocalDate));
      return this.scoreLocalService.isCurrentScore(score) && ageMs >= RS01_MINIMUM_DELAY_MS;
    }
    // RS02: today's Score has reset after six hours - the overnight reset alone doesn't count.
    return this.scoreLocalService.isScoreFromToday(score) && !this.scoreLocalService.isCurrentScore(score);
  }

  private openPrompt(step: OnboardingStep | null): void {
    this.markPresented(RS01_LESSON_ID, step, false);
    this.promptOpen = true;
    // The engine tracks started / presented and each button press for lessonId.
    this.coachMarkService.show({
      target: RS01_ANCHOR,
      heading: RS_COPY.heading,
      message: RS_COPY.body,
      lessonId: RS01_LESSON_ID,
      primaryAction: {
        label: RS_COPY.primaryLabel,
        onClick: () => {
          this.promptOpen = false;
          this.complete(RS01_LESSON_ID);
          this.scoreLocalService.markScoreAgainEntry();
          this.router.navigate([SCORE_LANDING_ROUTE]);
        },
      },
      secondaryAction: {
        label: RS_COPY.secondaryLabel,
        onClick: () => {
          this.promptOpen = false;
          this.defer(RS01_LESSON_ID);
        },
      },
      onDismiss: () => {
        this.promptOpen = false;
        this.defer(RS01_LESSON_ID);
      },
    });
  }

  private presentCard(step: OnboardingStep | null): void {
    this.markPresented(RS02_LESSON_ID, step, true);
    this.card$.next({ ...RS_COPY });
    this.track(RS02_LESSON_ID, "onboarding_lesson_presented");
  }

  /** Records the view on the shared row. `trackStart`: the engine already tracks it for RS01. */
  private markPresented(route: RouteId, step: OnboardingStep | null, trackStart: boolean): void {
    this.step = step;
    this.entry.markPresented(route);
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: REPEAT_SCORING_STEP_KEY,
      lastViewedAt: now,
      ...(step?.startedAt ? {} : { startedAt: now }),
    });
    if (trackStart && !step?.startedAt) {
      this.track(route, "onboarding_lesson_state_changed", "started");
    }
  }

  private complete(route: RouteId, reason?: string): void {
    this.resolved = true;
    const now = new Date();
    this.onboardingStepService.upsertStep({ stepKey: REPEAT_SCORING_STEP_KEY, lastViewedAt: now, completedAt: now });
    this.track(
      route,
      "onboarding_lesson_state_changed",
      `completed; milestone: ${SCORE_REPEAT_SCORING_INTRODUCED}${reason ? `; reason: ${reason}` : ""}`
    );
  }

  private defer(route: RouteId): void {
    const deferralCount = (this.step?.deferralCount ?? 0) + 1;
    const dismissed = deferralCount >= RS_MAX_DEFERRALS;
    if (dismissed) {
      this.resolved = true;
    }
    this.step = { ...(this.step ?? { stepKey: REPEAT_SCORING_STEP_KEY, dismissed: false }), deferralCount };
    this.onboardingStepService.upsertStep({
      stepKey: REPEAT_SCORING_STEP_KEY,
      lastViewedAt: new Date(),
      deferredUntil: DateHelper.getNextDayBoundary(),
      incrementDeferral: true,
      ...(dismissed ? { dismissed: true } : {}),
    });
    this.track(
      route,
      "onboarding_lesson_state_changed",
      `${dismissed ? "dismissed" : "deferred"}; deferral_count: ${deferralCount}`
    );
  }

  private settle(presented: boolean): void {
    this.outcome$?.next(presented);
    this.outcome$?.complete();
    this.outcome$ = null;
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(route: RouteId, eventName: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${route}; ${detail}` : `lesson: ${route}`,
    });
  }
}
